<template>
    <div v-if="isPdf">
        <div class="page-container" v-for="pageNum in pageCount" :key="pageNum">
            <canvas ref="pageCanvases"></canvas>
        </div>
    </div>
    <div v-else>
        <img :src="pdflink" class="w-100">
    </div>
</template>

<style lang="scss" scoped>
.page-container {
    background-color: none;
    text-align: center;
    padding: 10px;
}

.page-container canvas, img {
    border: 1px solid lightgray;
    direction: ltr;
    width: 100%;
}

</style>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from "vue";

import loadPdfjs from "../pdfjs-loader";
import { expectPdfRender, settlePdfRender } from "../pdf-render-tracker";

const props = defineProps({
    pdflink: { type: String, default: undefined },
    isPdf: { type: Boolean, default: false },
});

// shallowRef: a deep ref proxies pdf.js internals and breaks their private-field access
const pdfDoc = shallowRef(null);
const scale = 1.5;
const pageCanvases = ref([]);
const pageCount = computed(() => pdfDoc.value?.numPages ?? 0);

let loadVersion = 0;
let unmounted = false;
let settleCurrentRender = props.isPdf ? trackRender() : () => {};

onMounted(async () => {
    if (props.isPdf) {
        await loadPdf();
    }
});

watch(() => [props.pdflink, props.isPdf], async () => {
    settleCurrentRender();
    settleCurrentRender = props.isPdf ? trackRender() : () => {};
    await loadPdf();
}, { flush: "sync" });

onBeforeUnmount(() => {
    unmounted = true;
    loadVersion += 1;
    settleCurrentRender();
});

function trackRender() {
    expectPdfRender();
    let settled = false;
    return () => {
        if (!settled) {
            settled = true;
            settlePdfRender();
        }
    };
}

function isCurrentRender(version) {
    return !unmounted && version === loadVersion;
}

async function loadPdf() {
    const version = ++loadVersion;
    const settle = settleCurrentRender;
    pdfDoc.value = null;

    try {
        if (!props.isPdf || !props.pdflink) {
            return;
        }
        const pdfjs = await loadPdfjs();
        if (!isCurrentRender(version)) {
            return;
        }
        const document = await pdfjs.getDocument({ url: props.pdflink }).promise;
        if (!isCurrentRender(version)) {
            return;
        }
        pdfDoc.value = document;
        await nextTick();
        for (let pageNum = 1; pageNum <= document.numPages; pageNum += 1) {
            if (!isCurrentRender(version)) {
                return;
            }
            await renderPage(document, pageNum, version);
        }
    } catch (error) {
        if (isCurrentRender(version)) {
            throw error;
        }
    } finally {
        settle();
    }
}

async function renderPage(document, pageNum, version) {
    const page = await document.getPage(pageNum);
    if (!isCurrentRender(version)) {
        return;
    }
    const canvas = pageCanvases.value[pageNum - 1];
    const viewport = page.getViewport({ scale });
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
}
</script>
